import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMusic, FaDrum, FaRegCalendarAlt } from 'react-icons/fa';

// Import the logo directly - keep this as the only place using logo.jpg
import logoImage from '../assets/images/logo.jpg';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Main navigation links
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Institute', path: '/institute', icon: <FaDrum className="inline-block mr-1" /> },
    { name: 'Wedding', path: '/wedding-services', icon: <FaRegCalendarAlt className="inline-block mr-1" /> },
    { name: 'Corporate', path: '/corporate-events', icon: <FaMusic className="inline-block mr-1" /> },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled || location.pathname !== '/' 
          ? 'bg-white shadow-md py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex-shrink-0 flex items-center group">
            <motion.div
              whileHover={{ rotate: 15 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={logoImage} 
                alt="Rivisara Logo" 
                className="h-14 w-auto rounded-full overflow-hidden border-2 border-orange-400"
              />
            </motion.div>
            <div className="ml-3">
              <span className={`font-bold text-2xl font-display ${
                scrolled || location.pathname !== '/' ? 'text-orange-600' : 'text-white'
              }`}>
                Rivisara
              </span>
              <span className={`block text-xs ${
                scrolled || location.pathname !== '/' ? 'text-gray-600' : 'text-white text-opacity-90'
              }`}>
                Sri Lankan Drums & Dance
              </span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center ${
                  isActive(link.path) 
                    ? 'bg-orange-500 text-white' 
                    : scrolled || location.pathname !== '/' 
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50' 
                      : 'text-white hover:bg-white/10'
                } transition-all`}
              >
                {link.icon && <span className="mr-1">{link.icon}</span>}
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact#booking"
              className="ml-4 px-5 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md shadow-md transition-all flex items-center"
            >
              <span className="mr-1">✨</span>
              Book Now
            </Link>
          </div>
          
          <div className="md:hidden flex items-center">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                scrolled || location.pathname !== '/' ? 'text-gray-700 hover:bg-orange-50' : 'text-white hover:bg-white/10'
              } focus:outline-none transition-colors`}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white shadow-lg"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive(link.path) 
                      ? 'bg-orange-500 text-white' 
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.icon && <span className="mr-1">{link.icon}</span>}
                  {link.name}
                </Link>
              ))}
              <Link 
                to="/contact#booking" 
                className="block px-3 py-2 rounded-md text-base font-medium text-white bg-orange-600 hover:bg-orange-700 text-center shadow-sm"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="mr-1">✨</span>
                Book Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
